/**
 * Search Tool - Cluster Reply Showcase
 *
 * Searches the Cluster Reply knowledge base (webscraped site + additional material,
 * chunked by ## sections). Uses the shared data layer in src/data/cluster-reply.
 */

import { ToolSchema, ToolImplementation, ToolResult } from '../tool-types';
import { getClusterReplyAllChunks } from '@/data/cluster-reply';

/**
 * Simple keyword-based search over Cluster Reply chunks (same pattern as Vodafone).
 */
function simpleSearch(query: string): Array<{ id: string; title: string; content: string }> {
  const queryLower = query.toLowerCase();
  const chunks = getClusterReplyAllChunks();
  const results: Array<{ id: string; title: string; content: string; score: number }> = [];

  for (const { id, title, content } of chunks) {
    let score = 0;
    const titleLower = title.toLowerCase();
    const contentLower = content.toLowerCase();

    if (titleLower.includes(queryLower)) score += 10;
    if (contentLower.includes(queryLower)) score += 5;

    const words = queryLower.split(/\s+/).filter((w) => w.length > 2);
    for (const word of words) {
      if (titleLower.includes(word)) score += 2;
      if (contentLower.includes(word)) score += 1;
    }

    if (score > 0) results.push({ id, title, content, score });
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, 5).map(({ id, title, content }) => ({ id, title, content }));
}

export const searchToolClusterReplySchema: ToolSchema = {
  type: 'function',
  name: 'search',
  description:
    'Search the Cluster Reply knowledge base. It contains information about Cluster Reply (Microsoft partner, cloud, AI, Dynamics 365, GitHub, Power Platform, etc.). ' +
    'Results are formatted as a source id in square brackets, then the text content, and "-----" at the end of each result.',
  parameters: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query to find relevant information in the knowledge base'
      }
    },
    required: ['query'],
    additionalProperties: false
  }
};

export const searchToolClusterReplyImplementation: ToolImplementation = async (
  args: any
): Promise<ToolResult> => {
  try {
    const { query } = args;

    if (!query || typeof query !== 'string') {
      return {
        success: false,
        error: 'Invalid query parameter. Expected a non-empty string.'
      };
    }

    console.log(`🔍 Searching Cluster Reply knowledge base for: "${query}"`);

    const results = simpleSearch(query);

    if (results.length === 0) {
      return {
        success: true,
        data: 'No results found for your query. Please try different search terms.'
      };
    }

    const formattedResults = results
      .map((r) => `[${r.id}]: ${r.content}`)
      .join('\n-----\n');

    console.log(`✅ Found ${results.length} results`);

    return {
      success: true,
      data: formattedResults
    };
  } catch (error) {
    console.error('❌ Cluster Reply search tool error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during search'
    };
  }
};
